import { FunctionComponent, useState, useEffect } from "react";
import CustomModal from "../CustomModal";
import Column from "../../Column";
import Text from "../../Text";
import styled from "styled-components";
import { styles } from "../../../styles/themes/style";
import { useConnectionsHandlers } from "../../../services/actions/connections/useConnectionsHandlers";
import Input from "../../Input";
import Select from "../../select/Select";
import { useAppHandlers } from "../../../services/actions/app/useAppHandlers";
import FormControl from "../../FormControl";

interface ICreateCustomFieldModalProps {
  onClose?: () => void;
  editField?: {
    key: string;
    label: string;
    type: string;
  };
}

const fieldTypeOptions = [
  { value: "single_line_text", label: "Single-line text" },
  { value: "rich_text", label: "Rich text" },
  { value: "number", label: "Number" },
  { value: "date", label: "Date" },
  { value: "email", label: "Email" },
  { value: "url", label: "URL" },
  { value: "phone", label: "Phone" },
  { value: "user", label: "Member" },
  //{ value: 'select', label: 'Select' },
  //{ value: 'multiselect', label: 'Multiselect' }
];

const CreateCustomFieldModal: FunctionComponent<
  ICreateCustomFieldModalProps
> = ({ onClose, editField }) => {
  const {
    createCustomFieldDefinitionHandler,
  } = useConnectionsHandlers();
  const { hideModal } = useAppHandlers();
  const [fieldName, setFieldName] = useState(editField?.label || "");
  const [selectedType, setSelectedType] = useState<{
    value: string;
    label: string;
  } | null>(
    editField
      ? fieldTypeOptions.find((option) => option.value === editField.type) ||
          null
      : null
  );
  const [submitting, setSubmitting] = useState(false);

  // Ensure selectedType is set when editing
  useEffect(() => {
    if (editField && !selectedType) {
      const typeOption = fieldTypeOptions.find(
        (option) => option.value === editField.type
      );
      if (typeOption) {
        setSelectedType(typeOption);
      }
    }
  }, [editField, selectedType]);

  const isFormValid =
    fieldName.trim() !== "" && (editField ? true : selectedType !== null);

  const handleCreate = async () => {
    if (!isFormValid || submitting) return;

    setSubmitting(true);

    try {
      const result = await createCustomFieldDefinitionHandler(
        fieldName.trim(),
        selectedType?.value || editField?.type || "",
        editField ? editField.key : undefined
      );

      if (result?.success) {
        hideModal();
      }
    } catch (error) {
      console.error(
        `Error ${editField ? "updating" : "creating"} custom field:`,
        error
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <CustomModal
      headingText={editField ? "Edit custom field" : "Create custom field"}
      descriptionText={
        editField
          ? ""
          : "Create a new property for your contacts. Name it, choose a field type, and start using it across filters, views, imports, and exports."
      }
      closeButton
      onCancelHandler={onClose}
      confirmButton={{
        text: editField ? "Save changes" : "Create new field",
        disable: !isFormValid || submitting,
        isLoading: submitting,
        onClickHandler: handleCreate,
        customWidth: true,
      }}
    >
      <StyledColumn gap="20px">
        <Column gap="8px">
          <FieldLabel>PROPERTY NAME</FieldLabel>
          <StyledInput
            type="text"
            value={fieldName}
            onChange={(e: any) => setFieldName(e.target.value)}
            placeholder="Type name..."
            disabled={submitting}
          />
        </Column>

        <Column gap="8px">
          <FieldLabel>FIELD TYPE</FieldLabel>
          <FormControl $orientation="column">
            <Select
              placeholder="Select a field type"
              isSearchable={false}
              options={fieldTypeOptions}
              value={selectedType}
              onChange={(option: any) => setSelectedType(option)}
              isDisabled={submitting || !!editField}
            />
          </FormControl>
        </Column>
      </StyledColumn>
    </CustomModal>
  );
};

const StyledColumn = styled(Column)`
  width: 100%;
`;

const StyledInput = styled(Input)`
  width: 100%;
  border: 2px solid ${styles.colors.black200};

  &:focus {
    border-color: ${styles.colors.primary600};
  }
`;

const FieldLabel = styled(Text)`
  font-size: 11px;
  font-weight: 700;
  color: ${styles.colors.black400};
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

export default CreateCustomFieldModal;
